import { $, showOverlay, hideOverlay } from "./dom";
import { createCarousel, type Carousel } from "./carousel";

/**
 * The "learn the hidden OR problem" reveal: a small carousel of explainer slides
 * inside an overlay, with prev/next buttons and a row of clickable dots.
 *
 * Every game's index.html follows the same markup contract:
 *   #learnOverlay  .overlay        the overlay wrapper (toggled via `.hidden`)
 *   #learnSlides   > .lslide       the slides (`.active` marks the visible one)
 *   #lDots         > .ldot         the dots (`.on` marks the current one, built here)
 *   #lPrev #lNext  buttons         disabled at the first / last slide
 *
 * IDs/classes are overridable, but the defaults match both shipped games.
 */
export interface LearnCarouselOptions {
  /** Number of slides. Defaults to the count of `.lslide` in the slides container. */
  slideCount?: number;
  overlayId?: string;
  slidesId?: string;
  dotsId?: string;
  prevId?: string;
  nextId?: string;
}

export interface LearnCarousel extends Carousel {
  /** Build the dots (once), reset to the first slide, and show the overlay. */
  open(): void;
  /** Hide the overlay. */
  close(): void;
}

export function createLearnCarousel(opts: LearnCarouselOptions = {}): LearnCarousel {
  const overlayId = opts.overlayId ?? "learnOverlay";
  const slidesId = opts.slidesId ?? "learnSlides";
  const dotsId = opts.dotsId ?? "lDots";
  const prevId = opts.prevId ?? "lPrev";
  const nextId = opts.nextId ?? "lNext";

  const slideEls = (): NodeListOf<Element> => document.querySelectorAll(`#${slidesId} .lslide`);
  const dotEls = (): NodeListOf<Element> => document.querySelectorAll(`#${dotsId} .ldot`);
  const count = opts.slideCount ?? slideEls().length;

  const carousel = createCarousel({
    count,
    render(index: number): void {
      slideEls().forEach((s, i) => s.classList.toggle("active", i === index));
      dotEls().forEach((d, i) => d.classList.toggle("on", i === index));
      ($(prevId) as HTMLButtonElement).disabled = index === 0;
      ($(nextId) as HTMLButtonElement).disabled = index === count - 1;
    },
  });

  function buildDots(): void {
    const wrap = $(dotsId);
    if (wrap.childElementCount) return;
    for (let i = 0; i < count; i++) {
      const b = document.createElement("button");
      b.className = "ldot";
      b.type = "button";
      b.setAttribute("aria-label", "Go to slide " + (i + 1));
      b.addEventListener("click", () => carousel.goto(i));
      wrap.appendChild(b);
    }
  }

  return {
    get index(): number {
      return carousel.index;
    },
    goto: carousel.goto,
    step: carousel.step,
    reset: carousel.reset,
    open(): void {
      buildDots();
      carousel.goto(0);
      showOverlay(overlayId);
    },
    close(): void {
      hideOverlay(overlayId);
    },
  };
}
